import { Icon } from '@iconify/react'
import { Box, IconButton, Badge as MuiBadge, Popover, Stack, Tooltip, useTheme } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/atoms/button'
import { MvTypography } from '@/components/atoms/mv-typography'
import { NOTIFICATION_POPOVER_LIMIT } from '../constants/notification.constants'
import { NOTIFICATION_MESSAGES, NOTIFICATION_ROUTE, notificationMetaOf } from '../constants/notification.ui.constants'
import { useOpenNotification } from '../hooks/useOpenNotification'
import { useActionMarkAllNotificationsRead } from '../services/actionMarkAllNotificationsRead.service'
import { useFetchListNotification } from '../services/fetchListNotification.service'
import { useFetchNotificationUnreadCount } from '../services/fetchNotificationUnreadCount.service'
import { relativeTime, senderName } from '../utils/notification.format'

/**
 * Lonceng topbar WMS (GEN-03): badge belum-dibaca (polling ~20 dtk), popover 8 notifikasi terbaru, "Tandai semua
 * dibaca" dan "Lihat semua" → Pusat Notifikasi. Klik item = tandai dibaca + buka dokumen tujuan.
 */
export const WmsNotificationBell = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const subtle = theme.colorToken.text.neutral.subtle
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const open = Boolean(anchor)
  const close = () => setAnchor(null)

  const { data: count } = useFetchNotificationUnreadCount()
  const { data: list, isLoading } = useFetchListNotification(
    { limit: NOTIFICATION_POPOVER_LIMIT },
    { poll: open, enabled: open }
  )
  const markAll = useActionMarkAllNotificationsRead()
  const openNotification = useOpenNotification(close)
  const unread = count?.unread ?? 0
  const rows = list?.rows ?? []

  return (
    <>
      <Tooltip title='Notifikasi'>
        <IconButton
          color='inherit'
          aria-haspopup='true'
          aria-label={unread ? `Notifikasi, ${unread} belum dibaca` : 'Notifikasi'}
          onClick={event => setAnchor(event.currentTarget)}
          data-testid='notif-bell'
        >
          <MuiBadge
            color='error'
            badgeContent={unread}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                top: 4,
                right: 4,
                boxShadow: `0 0 0 2px ${theme.colorToken.background.neutral.normal}`
              }
            }}
            componentsProps={{ badge: { 'data-testid': 'notif-badge' } as object }}
          >
            <Icon icon='tabler:bell' width={24} />
          </MuiBadge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 2,
              width: 'min(380px, calc(100vw - 32px))',
              border: `1px solid ${theme.colorToken.border.neutral.normal}`,
              borderRadius: 2
            }
          }
        }}
      >
        <Stack data-testid='notif-popover'>
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ px: 4, py: 3 }}>
            <MvTypography size='TITLE_MD' typeSize='PX'>
              Notifikasi
            </MvTypography>
            <MvTypography size='LABEL_SM_NORMAL' color={subtle} data-testid='notif-popover-unread'>
              {`${unread} belum dibaca`}
            </MvTypography>
          </Stack>
          <Box
            sx={{
              borderTop: `1px solid ${theme.colorToken.border.neutral.normal}`,
              maxHeight: '60vh',
              overflowY: 'auto'
            }}
          >
            {!isLoading && !rows.length && (
              <Box sx={{ p: 4 }}>
                <MvTypography size='BODY_SM_NORMAL' color={subtle}>
                  {NOTIFICATION_MESSAGES.popoverEmpty}
                </MvTypography>
              </Box>
            )}
            {rows.map(row => (
              <Stack
                key={row.id}
                direction='row'
                gap={3}
                alignItems='flex-start'
                onClick={() => openNotification(row)}
                data-testid='notif-popover-item'
                data-unread={row.read_at ? 'false' : 'true'}
                sx={{
                  px: 4,
                  py: 3,
                  cursor: 'pointer',
                  backgroundColor: row.read_at ? 'transparent' : theme.colorToken.background.primary.subtlest,
                  borderBottom: `1px solid ${theme.colorToken.border.neutral.normal}`,
                  '&:hover': { backgroundColor: theme.colorToken.background.neutral.subtle }
                }}
              >
                <Box sx={{ pt: 0.5, color: row.read_at ? subtle : theme.colorToken.text.primary.normal }}>
                  <Icon icon={notificationMetaOf(row.type).icon} width={20} />
                </Box>
                <Stack gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                  <MvTypography size={row.read_at ? 'BODY_SM_NORMAL' : 'BODY_SM_BOLDEST'}>{row.title}</MvTypography>
                  <MvTypography
                    size='LABEL_SM_NORMAL'
                    color={subtle}
                    sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {row.message}
                  </MvTypography>
                  <MvTypography size='LABEL_SM_NORMAL' color={subtle}>
                    {`${senderName(row.sender) || 'Sistem'} · ${relativeTime(row.date_created)}`}
                  </MvTypography>
                </Stack>
                {!row.read_at && (
                  <Box
                    sx={{ width: 8, height: 8, mt: 1.5, borderRadius: '50%', flexShrink: 0, bgcolor: 'primary.main' }}
                  />
                )}
              </Stack>
            ))}
          </Box>
          <Stack direction='row' justifyContent='space-between' sx={{ p: 2 }}>
            <Button
              content='textOnly'
              text='Tandai semua dibaca'
              size='small'
              variant='text'
              disabled={!unread || markAll.isPending}
              onClick={() => markAll.mutate()}
              data-testid='notif-popover-read-all'
            />
            <Button
              content='textOnly'
              text='Lihat semua'
              size='small'
              variant='outlined'
              onClick={() => {
                close()
                navigate(NOTIFICATION_ROUTE)
              }}
              data-testid='notif-popover-see-all'
            />
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
